export type LoopRemovalEdge = {
  source?: any;
  target?: any;
  sourceHandle?: any;
  targetHandle?: any;
  hidden?: boolean;
  data?: any;
  type?: string;
};

function normalizeId(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function parseEdgeData(edge: LoopRemovalEdge): any {
  if (!edge?.data) return {};
  if (typeof edge.data === "object") return edge.data;

  try {
    return JSON.parse(edge.data);
  } catch {
    return {};
  }
}

function isHiddenEdge(edge: LoopRemovalEdge): boolean {
  return Boolean(edge?.hidden || parseEdgeData(edge).hidden);
}

function isLoopBackEdge(edge: LoopRemovalEdge): boolean {
  return (
    typeof edge?.sourceHandle === "string" &&
    typeof edge?.targetHandle === "string" &&
    edge.sourceHandle.includes("__handle-top") &&
    edge.targetHandle.includes("__handle-top")
  );
}

export function isVisibleLoopRemovalEdge(edge: LoopRemovalEdge): boolean {
  return Boolean(
    edge &&
      !isHiddenEdge(edge) &&
      edge.type !== "hidden" &&
      edge.type !== "serialized-hidden" &&
      !isLoopBackEdge(edge)
  );
}

function handleIncludes(value: unknown, handleName: string): boolean {
  return typeof value === "string" && value.includes(`__handle-${handleName}`);
}

function addNeighbor(
  adjacency: Map<string, Set<string>>,
  source: string,
  target: string
): void {
  if (!adjacency.has(source)) {
    adjacency.set(source, new Set());
  }
  const neighbors = adjacency.get(source);
  if (neighbors) {
    neighbors.add(target);
  }
}

export function collectVisibleLoopRemovalOrigIds({
  loopOrigId,
  loopEndOrigId,
  edges,
}: {
  loopOrigId: string;
  loopEndOrigId: string | null;
  edges: LoopRemovalEdge[];
}): Set<string> {
  const result = new Set<string>();
  if (!loopOrigId || !Array.isArray(edges)) return result;

  const boundaryIds = new Set<string>(["start", loopOrigId]);
  if (loopEndOrigId) {
    boundaryIds.add(loopEndOrigId);
  }

  const seeds: string[] = [];
  const queuedSeeds = new Set<string>();
  const adjacency = new Map<string, Set<string>>();

  const addSeed = (id: string | null) => {
    if (!id || boundaryIds.has(id) || queuedSeeds.has(id)) return;
    queuedSeeds.add(id);
    seeds.push(id);
  };

  edges.forEach((edge) => {
    if (!isVisibleLoopRemovalEdge(edge)) return;

    const source = normalizeId(edge.source);
    const target = normalizeId(edge.target);
    if (!source || !target || source === target) return;

    if (
      source === loopOrigId &&
      target !== loopEndOrigId &&
      handleIncludes(edge.sourceHandle, "right")
    ) {
      addSeed(target);
    }

    if (
      loopEndOrigId &&
      target === loopEndOrigId &&
      source !== loopOrigId &&
      handleIncludes(edge.targetHandle, "left")
    ) {
      addSeed(source);
    }

    if (boundaryIds.has(source) || boundaryIds.has(target)) return;

    addNeighbor(adjacency, source, target);
    addNeighbor(adjacency, target, source);
  });

  const queue = [...seeds];
  while (queue.length) {
    const current = queue.shift();
    if (current && !result.has(current)) {
      result.add(current);
      const neighbors = adjacency.get(current);
      if (neighbors) {
        neighbors.forEach((next) => {
          if (!result.has(next)) {
            queue.push(next);
          }
        });
      }
    }
  }

  return result;
}
